import { Component, computed, effect, inject, OnInit } from '@angular/core';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { NgForOf, NgIf, SlicePipe } from '@angular/common';
import { TableModule } from 'primeng/table';
import { PaginatorModule } from 'primeng/paginator';
import { ButtonDirective } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { IndexIoService } from '../../services/index-io.service';
import { DocumentsDTO } from '../../models/dto/document-dto';
import { NewsAndNoticesfilterDto } from '../../models/dto/newsandnoticesfilter-dto';
import { NewsAndNoticesDTO } from '../../models/dto/news-and-notices-dto';

@Component({
  selector: 'bnpp-indx-resource',
  imports: [
    FormsModule,
    NgForOf,
    InputTextModule,
    NgIf,
    DropdownModule,
    TableModule,
    PaginatorModule,
    ButtonDirective,
    SlicePipe,
  ],
  templateUrl: './resource.component.html',
  styleUrl: './resource.component.scss',
  standalone: true,
})
export class ResourceComponent implements OnInit {
  private indexInfoService = inject(IndexIoService);
  searchText = '';
  documents: DocumentsDTO[] = [];
  newsAndNotices: NewsAndNoticesDTO[] = [];
  filteredData: NewsAndNoticesDTO[] = [];
  defaultSortField = 'publicationDate';
  protected first: number = 0;
  protected rows: number = 25;
  protected isFilterApplied = false;
  protected totatlRecordsCount = 0;
  defaultSortOrder = -1;
  filter: NewsAndNoticesfilterDto = {
    type: 'All',
    publicationDate: 'All',
    bloombergCode: 'All',
    eventType: 'All',
  };
  publicationDates: string[] = [
    'All',
    'Today',
    '1 Month',
    '3 Months',
    '1 Year',
  ];

  typeOptions = computed(() => {
    const newsAndNotices = this.indexInfoService.newsAndNoticesInfo();
    return ['All', ...new Set(newsAndNotices?.map((item) => item.newsType))];
  });

  bloombergCodesOptions = computed(() => {
    const newsAndNotices = this.indexInfoService.newsAndNoticesInfo();
    const bloombergCodesSet = new Set(
      newsAndNotices?.flatMap((item) =>
        item.bloombergCode.split(',').map((code) => code.trim())
      )
    );
    return ['All', '-', ...Array.from(bloombergCodesSet).sort()];
  });

  eventTypesOptions = computed(() => {
    const newsAndNotices = this.indexInfoService.newsAndNoticesInfo();
    return [
      'All',
      '-',
      ...new Set(newsAndNotices?.map((item) => item.eventType).filter(Boolean)),
    ];
  });

  constructor() {
    effect(() => {
      const docs = this.indexInfoService.genericDocumentsInfo();
      const newsAndNotices = this.indexInfoService.newsAndNoticesInfo();
      if (docs) {
        this.documents = docs;
      }
      if (newsAndNotices) {
        this.newsAndNotices = newsAndNotices;
        this.applyFilters(this.newsAndNotices, this.filter);
      }
    });
  }

  ngOnInit(): void {
    const selectedResident = localStorage.getItem('selectedResident')!;
    this.indexInfoService.getGenericDocuments();
    this.indexInfoService.getNewsAndNotices(selectedResident);
  }

  filterDocs() {
    if (this.searchText) {
      return this.documents.filter((d) =>
        d.Title.toLowerCase().includes(this.searchText.toLowerCase())
      );
    }
    return this.documents;
  }

  applyFilters(
    newsAndNoticesData: NewsAndNoticesDTO[] | null,
    filters: NewsAndNoticesfilterDto
  ) {
    if (!newsAndNoticesData) {
      this.filteredData = [];
      return;
    }
    this.totatlRecordsCount = newsAndNoticesData.length;

    const predicates: Array<(row: NewsAndNoticesDTO) => boolean> = [
      (row) => this.typeMatches(row, filters.type),
      (row) =>
        this.publicationDateMatches(
          row.publicationDate,
          filters.publicationDate
        ),
      (row) =>
        this.bloombergCodeMatches(row.bloombergCode, filters.bloombergCode),
      (row) => this.eventTypeMatches(row.eventType, filters.eventType),
    ];

    this.filteredData = newsAndNoticesData.filter((row) =>
      predicates.every((predicate) => predicate(row))
    );

    this.isFilterApplied = this.totatlRecordsCount !== this.filteredData.length;
  }

  private typeMatches(row: NewsAndNoticesDTO, type: string): boolean {
    return type === 'All' || row.newsType === type;
  }

  private publicationDateMatches(rowDate: string, filterDate: string): boolean {
    return (
      filterDate === 'All' || this.matchPublicationDate(rowDate, filterDate)
    );
  }

  private bloombergCodeMatches(rowCode: string, filterCode: string): boolean {
    if (filterCode === 'All') {
      return true;
    }

    if (filterCode === '-') {
      return !rowCode;
    }

    return this.matchBloombergCode(rowCode, filterCode);
  }

  private eventTypeMatches(
    rowEvent: string | null,
    filterEvent: string
  ): boolean {
    if (filterEvent === 'All') {
      return true;
    }

    if (filterEvent === '-') {
      return !rowEvent;
    }
    return rowEvent === filterEvent;
  }

  private matchBloombergCode(
    rowBloombergCodes: string,
    filterBloombergCode: string
  ): boolean {
    const rowCodes = rowBloombergCodes.split(',').map((code) => code.trim());
    return rowCodes.includes(filterBloombergCode);
  }

  private matchPublicationDate(
    publicationDate: string,
    filterPublicationDate: string
  ): boolean {
    const today = new Date();
    const pubDate = new Date(publicationDate);

    switch (filterPublicationDate) {
      case 'Today':
        return pubDate.toDateString() === today.toDateString();
      case '1 Month':
        return this.isWithinLastMonth(pubDate, today);
      case '3 Months':
        return this.isWithinLastThreeMonths(pubDate, today);
      case '1 Year':
        return this.isWithinLastYear(pubDate, today);
      default:
        return true;
    }
  }

  private isWithinLastMonth(date: Date, today: Date): boolean {
    const oneMonthAgo = new Date(today);
    oneMonthAgo.setMonth(today.getMonth() - 1);
    return date >= oneMonthAgo && date <= today;
  }

  private isWithinLastThreeMonths(date: Date, today: Date): boolean {
    const threeMonthsAgo = new Date(today);
    threeMonthsAgo.setMonth(today.getMonth() - 3);
    return date >= threeMonthsAgo && date <= today;
  }

  private isWithinLastYear(date: Date, today: Date): boolean {
    const oneYearAgo = new Date(today);
    oneYearAgo.setFullYear(today.getFullYear() - 1);
    return date >= oneYearAgo && date <= today;
  }

  setFilters() {
    this.applyFilters(this.newsAndNotices, this.filter);
  }

  onDownloadDocument(item: DocumentsDTO) {
    this.indexInfoService
      .downloadDocument(
        'Document',
        item.Path,
        item.Title,
        item.FileExtension,
        item.S3Key
      )
      .subscribe();
  }

  onDownloadNewsDocument(item: NewsAndNoticesDTO) {
    this.indexInfoService
      .downloadDocument(
        'News',
        item.documentId,
        item.fileName,
        'pdf',
        item.s3Key
      )
      .subscribe();
  }

  resetFilter() {
    this.filter = {
      type: 'All',
      publicationDate: 'All',
      bloombergCode: 'All',
      eventType: 'All',
    };
    this.setFilters();
  }
}
